import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Box, Button, Dialog, Loader, Typography } from '@screentone/core';
import cloneDeep from 'lodash/cloneDeep';

import { WidePageWrapper } from 'components';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { NewsletterDto, NewsletterPageContainer } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { NewsletterMultitabSection } from './components/newsletter-multitab-section/NewsletterMultitabSection';
import { TitleHeader } from './components/title-header/TitleHeader';
import { useNewsletterPublish } from './hooks/useNewsletterPublish';

export const ReadOnlyNewsletter = ({ isScheduled }: { isScheduled: boolean }) => {
  const [sendNowOpen, setSendNowOpen] = useState<boolean>(false);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const {
    root: data,
    metadata,
    renderEntity,
    getSortedEmbargoContent
  } = useDataModelContext<NewsletterPageContainer>();

  const { data: publicationSettingResp } = usePublicationSettings();
  const navigate = useNavigate();
  const { currentProperty: routeProperty } = useAuth(); // Original property from URL for navigation

  const { handleSendTest, handleSendSchedule, handleReschedule, handleDeleteSchedule } = useNewsletterPublish(
    publicationSettingResp?.publicationSetting
  );

  const handleSendTestClick = async (emails: string) => {
    await handleSendTest(emails);
  };

  const handleSendNowClick = async () => {
    setIsSending(true);
    try {
      await handleSendSchedule();
      setSendNowOpen(false);
      navigate(`/${routeProperty}/newsletters/${metadata.allessehId}`);
    } catch {
      // error already shown via alertError in handleSendSchedule
    } finally {
      setIsSending(false);
    }
  };

  const handleRescheduleClick = async (publishUtc: number) => {
    const schedule = await handleReschedule(publishUtc);

    if (schedule) {
      navigate(`/${routeProperty}/newsletters/scheduled/${metadata.allessehId}/${schedule.publishUtc}`);
    }
  };

  const handleDeleteClick = async () => {
    await handleDeleteSchedule();

    navigate(`/${routeProperty}/newsletters`);
  };

  const latestEmbargoContent = getSortedEmbargoContent('desc').at(0);

  return (
    <WidePageWrapper padding={{ all: 'md' }}>
      <WidePageWrapper.Header>
        <TitleHeader
          readOnly
          isScheduled={isScheduled}
          newsletterName={metadata.name}
          newsletterId={metadata.allessehId}
          handleScheduleClick={handleRescheduleClick}
          handleSendNowClick={() => {
            setSendNowOpen(true);
          }}
          handleSendTestClick={handleSendTestClick}
          publicationSetting={publicationSettingResp?.publicationSetting}
          latestEmbargoContent={latestEmbargoContent}
        />

        <Dialog status={sendNowOpen ? 'open' : 'closed'} onDismiss={() => setSendNowOpen(false)}>
          <Dialog.Title id="dialogTitle">Send Campaign</Dialog.Title>
          <Dialog.Content id="dialogDesc">
            <Typography variant="header4">{data.attributes.subject}</Typography>
            <Typography>Are you sure you want to send this immediately?</Typography>
          </Dialog.Content>
          <Dialog.Actions>
            <Button secondary onClick={() => setSendNowOpen(false)} disabled={isSending}>
              Cancel
            </Button>
            <Button primary onClick={handleSendNowClick} disabled={isSending}>
              {isSending ? (
                <>
                  <Loader size="md" margin={{ right: 'xs' }} />
                  Sending...
                </>
              ) : (
                'Send'
              )}
            </Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog status={cancelModalOpen ? 'open' : 'closed'} onDismiss={() => setCancelModalOpen(false)}>
          <Dialog.Title id="dialogTitle">Cancel Campaign</Dialog.Title>
          <Dialog.Content id="dialogDesc">
            <Typography>Are you sure you want to cancel this campaign?</Typography>
          </Dialog.Content>
          <Dialog.Actions>
            <Button secondary onClick={() => setCancelModalOpen(false)}>
              Keep Campaign
            </Button>
            <Button primary onClick={handleDeleteClick} data-testid="newsletter-cancel-campaign">
              Cancel Campaign
            </Button>
          </Dialog.Actions>
        </Dialog>
      </WidePageWrapper.Header>
      <WidePageWrapper.Left>
        {renderEntity(cloneDeep(data), {
          hierarchyId: '',
          withoutDragNDrop: true
        })}
        <Box margin={{ top: 'md', bottom: 'md' }}>
          <Box.Title data-testid="newsletters-custom-title">Custom Content</Box.Title>
          <Box.Content data-testid="newsletters-custom-content-data">
            <Typography variant="label3">Sender Name</Typography>
            <Typography data-testid="newsletter-custom-sender-name" margin={{ bottom: 'md' }}>
              {data.attributes.senderName}
            </Typography>
            <Typography variant="label3">Reply-To Email</Typography>
            <Typography data-testid="newsletter-custom-reply-email" margin={{ bottom: 'md' }}>
              {data.attributes.replyToEmail}
            </Typography>
            <Typography variant="label3">Subject Line</Typography>
            <Typography data-testid="newsletter-custom-subject-line" margin={{ bottom: 'md' }}>
              {data.attributes.subject}
            </Typography>
            <Typography variant="label3">Pre-Header Text</Typography>
            <Typography data-testid="newsletter-custom-header-text" margin={{ bottom: 'md' }}>
              {data.attributes.preHeaderText}
            </Typography>
          </Box.Content>
        </Box>
      </WidePageWrapper.Left>
      <WidePageWrapper.Right>
        <NewsletterMultitabSection
          readOnly
          isScheduled={isScheduled}
          campaign={{ root: data, metadata } as NewsletterDto}
          cancelCampaign={() => {
            setCancelModalOpen(true);
          }}
          publicationSetting={publicationSettingResp?.publicationSetting}
        />
      </WidePageWrapper.Right>
    </WidePageWrapper>
  );
};
